import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Shield } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { StatusBadge, LeagueChip } from "@/components/badges";

// Global search — players AND teams, querying /api/search (matches name, team, abbr, position, league).
export function PlayerSearch({ open, onOpenChange }) {
  const [q, setQ] = useState("");
  const [players, setPlayers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const active = useRef(0);

  useEffect(() => { if (!open) { setQ(""); setPlayers([]); setTeams([]); } }, [open]);

  useEffect(() => {
    if (!q.trim()) { setPlayers([]); setTeams([]); setLoading(false); return; }
    setLoading(true);
    const token = ++active.current;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/search", { params: { q: q.trim(), limit: 8 } });
        if (token === active.current) { setPlayers(data.players); setTeams(data.teams); }
      } catch {
        if (token === active.current) { setPlayers([]); setTeams([]); }
      } finally {
        if (token === active.current) setLoading(false);
      }
    }, 220);
    return () => clearTimeout(t);
  }, [q]);

  const goPlayer = (id) => { onOpenChange(false); navigate(`/players/${id}`); };
  const goTeam = (id) => { onOpenChange(false); navigate(`/teams/${id}`); };
  const firstResult = () => { if (teams[0]) goTeam(teams[0].id); else if (players[0]) goPlayer(players[0].id); };
  const empty = !loading && q.trim() && players.length === 0 && teams.length === 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="p-0 gap-0 bg-[#0F1420] border-[#232D42] max-w-lg overflow-hidden" data-testid="player-search-dialog">
        <DialogTitle className="sr-only">Search players and teams</DialogTitle>
        <DialogDescription className="sr-only">Search across the NFL, NBA, NHL and MLB by player, team, position or league.</DialogDescription>
        <div className="flex items-center gap-2 border-b border-[#232D42] px-4">
          <Search size={18} className="text-slate-500 shrink-0" />
          <input data-testid="player-search-input" autoFocus value={q} onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") firstResult(); }}
            placeholder="Search players, teams, positions or a league…"
            className="flex-1 bg-transparent py-4 text-sm text-slate-100 outline-none placeholder:text-slate-600" />
        </div>
        <div className="max-h-[380px] overflow-y-auto py-1" data-testid="player-search-results">
          {loading && <p className="px-4 py-6 text-center text-sm text-slate-500 font-mono">Searching…</p>}
          {empty && (
            <div className="px-4 py-6 text-center">
              <p className="text-sm text-slate-500 mb-2">No matches for “{q}”.</p>
              <button data-testid="search-request-btn"
                onClick={() => window.dispatchEvent(new CustomEvent("sq:open-feedback", { detail: { prefill: `Please add: ${q}` } }))}
                className="text-xs font-mono text-[#00E5A3] hover:underline">
                Can't find them? Ask me to add it →
              </button>
            </div>
          )}
          {!loading && !q.trim() && (
            <p className="px-4 py-6 text-center text-xs text-slate-600 font-mono">Try “McDavid”, “Lakers”, “QB”, or a league like “NHL”.</p>
          )}

          {teams.length > 0 && (
            <div>
              <p className="px-4 pt-2 pb-1 text-[10px] font-mono uppercase tracking-widest text-slate-600">Teams</p>
              {teams.map((t) => (
                <button key={t.id} data-testid={`search-team-${t.id}`} onClick={() => goTeam(t.id)}
                  className="w-full flex items-center gap-2 px-4 py-2.5 hover:bg-[#1C2438] text-left">
                  <Shield size={15} className="text-slate-500 shrink-0" />
                  <LeagueChip league={t.league} />
                  <span className="text-sm text-slate-100 truncate">{t.name}</span>
                  <span className="text-xs text-slate-500 font-mono ml-auto">{t.abbr}</span>
                </button>
              ))}
            </div>
          )}

          {players.length > 0 && (
            <div>
              <p className="px-4 pt-2 pb-1 text-[10px] font-mono uppercase tracking-widest text-slate-600">Players</p>
              {players.map((p) => (
                <button key={p.id} data-testid={`player-search-result-${p.id}`} onClick={() => goPlayer(p.id)}
                  className="w-full flex items-center justify-between gap-3 px-4 py-2.5 hover:bg-[#1C2438] text-left">
                  <div className="flex items-center gap-2 min-w-0">
                    <LeagueChip league={p.league} />
                    <span className="text-sm text-slate-100 truncate">{p.name}</span>
                    <span className="text-xs text-slate-500 font-mono shrink-0">{p.team_abbr} · {p.position}</span>
                  </div>
                  <StatusBadge status={p.status} />
                </button>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
